import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IoTimerOutline,
  IoTrendingUpOutline,
  IoCheckmarkCircleOutline,
  IoPeople,
  IoAlertCircle,
  IoAdd,
  IoRefresh,
  IoTicket,
  IoFlash,
  IoCalendar,
} from 'react-icons/io5';
import TicketCard from '../components/TicketCard';
import { fetchTickets, fetchUsers } from '../api/api';
import { useSocket } from '../context/SocketContext';
import './Dashboard.css';

const normalizeTicket = (t) => ({
  id: t.id,
  subject: t.title,
  title: t.title,
  description: t.description,
  status: (t.status || 'open').replace('-', ' '),
  priority: (t.priority || 'medium').toLowerCase(),
  customerName: t.created_by_name || 'Customer',
  createdAt: t.created_at || t.createdAt,
  replies: 0,
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const socket = useSocket();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([fetchTickets(), fetchUsers()])
      .then(([ticketsData, usersData]) => {
        setTickets(Array.isArray(ticketsData) ? ticketsData : []);
        setUsers(Array.isArray(usersData) ? usersData : []);
      })
      .catch(() => {
        setTickets([]);
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!socket) return;
    const onUpdate = () => loadData();
    socket.on('ticket:created', onUpdate);
    socket.on('ticket:assigned', onUpdate);
    socket.on('ticket:updated', onUpdate);
    return () => {
      socket.off('ticket:created', onUpdate);
      socket.off('ticket:assigned', onUpdate);
      socket.off('ticket:updated', onUpdate);
    };
  }, [socket, loadData]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = tickets.length;
    const pending = tickets.filter((t) => t.status === 'open').length;
    const inProgress = tickets.filter((t) => t.status === 'in-progress').length;
    const resolved = tickets.filter((t) => t.status === 'resolved').length;
    const closed = tickets.filter((t) => t.status === 'closed').length;
    const completed = resolved + closed;
    const highPriority = tickets.filter((t) => t.priority === 'high').length;
    const totalUsers = users.length;
    const activeUsers = users.filter((u) => u.is_active !== false).length;

    return {
      total,
      pending,
      inProgress,
      resolved,
      closed,
      completed,
      highPriority,
      totalUsers,
      activeUsers,
    };
  }, [tickets, users]);

  const recentTickets = tickets
    .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
    .slice(0, 6)
    .map(normalizeTicket);


  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header-enhanced">
        <div className="dashboard-header-title">
          <h1>Admin Dashboard</h1>
          <p>Overview of support tickets and system status</p>
        </div>
        <button className="dashboard-refresh-btn" onClick={loadData} disabled={loading}>
          <IoRefresh className={loading ? 'spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-stats-grid">
        {/* Ticket Stats */}
        <div className="stat-card-large tickets-total">
          <div className="stat-card-icon">
            <IoTicket />
          </div>
          <div className="stat-card-content">
            <span className="stat-value-large">{loading ? '—' : stats.total}</span>
            <span className="stat-label-large">Total Tickets</span>
          </div>
        </div>

        <div className="stat-card-large pending">
          <div className="stat-card-icon">
            <IoTimerOutline />
          </div>
          <div className="stat-card-content">
            <span className="stat-value-large">{loading ? '—' : stats.pending}</span>
            <span className="stat-label-large">Pending</span>
          </div>
        </div>

        <div className="stat-card-large in-progress">
          <div className="stat-card-icon">
            <IoTrendingUpOutline />
          </div>
          <div className="stat-card-content">
            <span className="stat-value-large">{loading ? '—' : stats.inProgress}</span>
            <span className="stat-label-large">In Progress</span>
          </div>
        </div>

        <div className="stat-card-large resolved">
          <div className="stat-card-icon">
            <IoCheckmarkCircleOutline />
          </div>
          <div className="stat-card-content">
            <span className="stat-value-large">{loading ? '—' : stats.resolved}</span>
            <span className="stat-label-large">Resolved</span>
          </div>
        </div>

        <div className="stat-card-large high-priority">
          <div className="stat-card-icon">
            <IoAlertCircle />
          </div>
          <div className="stat-card-content">
            <span className="stat-value-large">{loading ? '—' : stats.highPriority}</span>
            <span className="stat-label-large">High Priority</span>
          </div>
        </div>

        <div className="stat-card-large users-total">
          <div className="stat-card-icon">
            <IoPeople />
          </div>
          <div className="stat-card-content">
            <span className="stat-value-large">{loading ? '—' : stats.totalUsers}</span>
            <span className="stat-label-large">Total Users</span>
            <span className="stat-sublabel">{stats.activeUsers} active</span>
          </div>
        </div>
      </div>

      {/* Recent Tickets Section */}
      <div className="dashboard-section-enhanced">
        <div className="section-header-enhanced">
          <div className="section-title">
            <IoCalendar className="section-icon" />
            <h2>Recent Tickets</h2>
          </div>
          <button className="view-all-btn-enhanced" onClick={() => navigate('/admin/tickets')}>
            View All Tickets
          </button>
        </div>
        <div className="tickets-grid-enhanced">
          {loading ? (
            <div className="dashboard-loading">
              <div className="loading-spinner"></div>
              <p>Loading dashboard...</p>
            </div>
          ) : recentTickets.length > 0 ? (
            recentTickets.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} />)
          ) : (
            <div className="dashboard-empty">
              <div className="empty-icon-large"><IoTicket /></div>
              <h3>No tickets yet</h3>
              <p>New tickets will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
